<script setup>
import { reactive, computed, onMounted, onBeforeUnmount, watch, ref, nextTick } from 'vue'

import { useGlobalStore } from '@/stores/useGlobalStore'
import { useUserStore } from '@/stores/useUserStore'
import { useSpaceStore } from '@/stores/useSpaceStore'
import { useGroupStore } from '@/stores/useGroupStore'

import PrivacyButton from '@/components/PrivacyButton.vue'
import ShareOptions from '@/components/ShareOptions.vue'
import SpaceUsers from '@/components/dialogs/SpaceUsers.vue'
import RssFeeds from '@/components/dialogs/RssFeeds.vue'
import Embed from '@/components/dialogs/Embed.vue'
import utils from '@/utils.js'
import ImportExportButton from '@/components/ImportExportButton.vue'
import consts from '@/consts.js'
import AddToGroup from '@/components/dialogs/AddToGroup.vue'
import GroupLabel from '@/components/GroupLabel.vue'

const globalStore = useGlobalStore()
const userStore = useUserStore()
const spaceStore = useSpaceStore()
const groupStore = useGroupStore()

const dialog = ref(null)

onMounted(() => {
  window.addEventListener('resize', updateDialogHeight)
})
onBeforeUnmount(() => {
  window.removeEventListener('resize', updateDialogHeight)
})

const props = defineProps({
  visible: Boolean
})

watch(() => props.visible, (value, prevValue) => {
  globalStore.clearNotificationsWithPosition()
  closeDialogs()
  if (value) {
    updateDialogHeight()
    globalStore.shouldExplicitlyHideFooter = true
    spaceStore.updateSpacePreviewImage()
  } else {
    globalStore.shouldExplicitlyHideFooter = false
  }
})

const state = reactive({
  urlIsCopied: false,
  privacyPickerIsVisible: false,
  dialogHeight: null,
  rssFeedsIsVisible: false,
  embedIsVisible: false,
  childDialogIsVisible: false,
  spaceUsersIsVisible: false,
  addToGroupIsVisible: false
})

const isSecureAppContextIOS = computed(() => consts.isSecureAppContextIOS)
const currentUserIsSignedIn = computed(() => userStore.getUserIsSignedIn)
const isSpaceMember = computed(() => userStore.getUserIsSpaceMember)
const spaceIsRemote = computed(() => spaceStore.getSpaceIsRemote)

// dialog

const updateDialogHeight = () => {
  if (!props.visible) { return }
  nextTick(() => {
    const element = dialog.value
    state.dialogHeight = utils.elementHeight(element)
  })
}
const dialogIsVisible = computed(() => {
  return (
    state.privacyPickerIsVisible ||
    state.rssFeedsIsVisible ||
    state.embedIsVisible ||
    state.childDialogIsVisible ||
    state.spaceUsersIsVisible ||
    state.addToGroupIsVisible
  )
})
const closeDialogs = () => {
  state.privacyPickerIsVisible = false
  state.rssFeedsIsVisible = false
  state.embedIsVisible = false
  state.childDialogIsVisible = false
  state.spaceUsersIsVisible = false
  state.addToGroupIsVisible = false
  globalStore.triggerCloseChildDialogs()
}
const childDialogIsVisible = (value) => {
  state.childDialogIsVisible = value
  state.embedIsVisible = false
}
const selectGroup = (group) => {
  closeDialogs()
}

// toggles

const triggerSignUpOrInIsVisible = () => {
  globalStore.closeAllDialogs()
  globalStore.triggerSignUpOrInIsVisible()
}
const togglePrivacyPickerIsVisible = () => {
  const isVisible = state.privacyPickerIsVisible
  closeDialogs()
  state.privacyPickerIsVisible = !isVisible
}
const toggleRssFeedsIsVisible = () => {
  const isVisible = state.rssFeedsIsVisible
  closeDialogs()
  state.rssFeedsIsVisible = !isVisible
}
const toggleEmbedIsVisible = () => {
  const isVisible = state.embedIsVisible
  closeDialogs()
  state.embedIsVisible = !isVisible
}
const toggleAddToGroupIsVisible = () => {
  const isVisible = state.addToGroupIsVisible
  closeDialogs()
  state.addToGroupIsVisible = !isVisible
}

// users

const toggleSpaceUsersIsVisible = () => {
  const value = !state.spaceUsersIsVisible
  closeDialogs()
  state.spaceUsersIsVisible = value
}

// group

const userGroups = computed(() => groupStore.getCurrentUserGroups)
const spaceGroups = computed(() => groupStore.getCurrentSpaceGroups)
const currentUserIsGroupAdmin = (group) => {
  return groupStore.getGroupUserIsAdmin({
    userId: userStore.id,
    groupId: group.id
  })
}
const toggleSpaceGroup = async (group) => {
  const shouldRemoveSpaceGroup = spaceGroups.value.find(spaceGroup => spaceGroup.id === group.id)
  if (shouldRemoveSpaceGroup) {
    await removeSpaceGroup(group)
  } else {
    await addSpaceToGroup(group)
  }
  // emit('selectGroup', group)
}
const addSpaceToGroup = async (group) => {
  const isSpaceCreator = userStore.getUserIsSpaceCreator
  if (isSpaceCreator) {
    await groupStore.addSpaceToGroup(group)
  } else {
    globalStore.addNotification({
      message: 'Only space creator can assign to group',
      type: 'danger'
    })
  }
}
const removeSpaceGroup = async (group) => {
  const isGroupAdmin = currentUserIsGroupAdmin(group)
  const isSpaceCreator = userStore.getUserIsSpaceCreator
  if (isGroupAdmin || isSpaceCreator) {
    await groupStore.removeSpaceFromGroup(group)
  } else {
    globalStore.addNotification({
      message: 'Only space creator, or group admin, can remove from group',
      type: 'danger'
    })
  }
}

</script>

<template lang="pug">
dialog.share.wide(v-if="props.visible" :open="props.visible" @click.left.stop="closeDialogs" ref="dialog" :style="{'max-height': state.dialogHeight + 'px'}" :class="{overflow: !dialogIsVisible}")
  section.title-section
    .row.title-row
      p Share
      .row
        //- privacy picker
        PrivacyButton(:privacyPickerIsVisible="state.privacyPickerIsVisible" :showShortName="true" :isSmallButton="true" @togglePrivacyPickerIsVisible="togglePrivacyPickerIsVisible" @closeDialogs="closeDialogs")
        //- users
        .button-wrap
          button.small-button(@click.stop="toggleSpaceUsersIsVisible" :class="{active: state.spaceUsersIsVisible}")
            span Users
          SpaceUsers(:visible="state.spaceUsersIsVisible")
        //- rss
        .button-wrap(v-if="spaceIsRemote")
          button.small-button(@click.left.stop="toggleRssFeedsIsVisible" :class="{ active: state.rssFeedsIsVisible }" title="RSS Feeds")
            span RSS
          RssFeeds(:visible="state.rssFeedsIsVisible")

  template(v-if="spaceIsRemote")
    section
      //- Group Picker
      .row.button-wrap(v-if="isSpaceMember")
        button.group-button(title="Add to Group" :class="{active: state.addToGroupIsVisible}" @click.left.prevent.stop="toggleAddToGroupIsVisible" @keydown.stop.enter="toggleAddToGroupIsVisible")
          img.icon.group(src="@/assets/group.svg")
          template(v-if="spaceGroups.length")
            GroupLabel(v-for="spaceGroup in spaceGroups" :key="spaceGroup.id" :group="spaceGroup" :showName="true")
          template(v-else)
            span Add to Group
        AddToGroup(:visible="state.addToGroupIsVisible" @selectGroup="toggleSpaceGroup" :groups="userGroups" :selectedGroups="spaceGroups" @closeDialogs="closeDialogs")

      //- Invite
      ShareOptions(@closeDialogs="closeDialogs" @childDialogIsVisible="childDialogIsVisible" @selectGroup="selectGroup")

    //- Import, Export, Embed
    section.import-export-section
      .row
        ImportExportButton(@childDialogIsVisible="childDialogIsVisible")
        //- Embed
        .button-wrap
          button(@click.left.stop="toggleEmbedIsVisible" :class="{ active: state.embedIsVisible }")
            span Embed
          Embed(:visible="state.embedIsVisible")
      details(@toggle="updateDialogHeight")
        summary Spread the Word
        section.subsection
          p I don't have the resources of a VC backed company, so when you tell a friend about Kinopio, or share spaces at work, it really helps.
          //- p Your voice is the water that grows this seedling.
          p – Piri
          p
            img(src="https://cdn.kinopio.club/fqoJozHGrobicZe0XUG1e/my-garden.webp")
          //- https://alternativeto.net/software/kinopio/about/
          //- https://toolfinder.co/tools/kinopio

  template(v-else)
    section
      p
        span To share or invite collaborators,
        span.badge.info you need to Sign Up or In
        span for your spaces to be synced and accessible anywhere.
      button(@click.left="triggerSignUpOrInIsVisible") Sign Up or In

</template>

<style lang="stylus">
dialog.share
  top calc(100% - 8px)
  left initial
  right 8px
  max-height calc(100vh - 25px)
  &.overflow
    overflow auto
  .badge
    display inline-block
    &.danger
      background-color var(--danger-background)
  .description
    margin-top 3px
  .group-button
    display flex
    flex-wrap wrap
    gap 4px
    align-items center
    .group-badge,
    .group-label
    > span
      margin 0

  dialog.user-details
    left initial
    right calc(100% - 20px)
  .share-private
    margin-top 10px
  .button-tip-badge
    top -12px
    pointer-events none
  p + .subsection
    margin-top 10px

  .segmented-buttons
    z-index 1
  .extra-options-button
    margin-left auto
    margin-top 0
    margin-bottom -2px
    width auto
    cursor pointer
    height 20px
    width initial
    &.label
      padding-top 2px
      input
        background-color transparent
        pointer-events none

  .private-copy-badge
    margin-left 6px
    vertical-align 1px
  .title-row
    p + .row
      margin-top 0
    label + label
      margin-left 6px
  dialog.privacy-picker,
  dialog.space-users
    top calc(100% - 4px)
    left initial
    right 8px
</style>
